// src/modules/app-views/_apps/appmulti/plans/pages/Init/components/PlansPreviewHeader/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Divider, Flex, Tag, Typography, theme } from 'antd';
import { ApiOutlined } from '@ant-design/icons';
import SimpleAvatar from 'components/SimpleComponents/SimpleAvatar';
import translateX from 'utils/translateX';
import plansService from 'modules/app-views/_apps/appmulti/plans/service';
import financialService from 'services/financialService';
import app from 'configs/ConfigIndex';

const { Text, Title } = Typography;

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const AVATAR_SIZE = 100;
const BG_IMAGE_PATH = '/img/others/bg-appmulti.jpg';

const STYLES = {
  bgStrip: {
    position: 'absolute',
    top: -24,
    left: -24,
    right: -24,
    height: 80,
    backgroundSize: 'cover',
    backgroundPosition: 'center bottom',
    backgroundRepeat: 'no-repeat',
  },
  avatarWrapper: {
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 10,
  },
  tagsRow: {
    marginBottom: 12,
    display: 'flex',
    justifyContent: 'center',
    gap: 4,
    flexWrap: 'wrap',
  },
  infoRows: { marginTop: 16 },
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const PlansPreviewHeader = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { selectedDocument: r } = props;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = theme.useToken();
  const baseColor = app?.baseColor || token.colorPrimary;

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  if (!r) return null;

  const op = r?.__operator;
  const isApiPlan = !!r?._integrations?.planId;
  const coverageCount = r?.coverages?.length || 0;

  let priceLabel;
  if (r?.priceType === 'table') {
    priceLabel = translateX('table');
  } else if (r?.price != null) {
    const symbol = plansService.getCurrencySymbol(r?.currency);
    const formatted = financialService.formatNumber(r.price, { decimals: 2, locale: 'pt-BR' });
    const typeLabel = r?.priceType === 'daily' ? `/${translateX('day')}` : '';
    priceLabel = `${symbol} ${formatted}${typeLabel}`;
  } else {
    priceLabel = '—';
  }

  const exchangeRate = op?.parameters?.__exchangeRate;
  const exchangeRateSuffix = op?.parameters?.__exchangeRateSuffix;
  let exchangeRateLabel;
  if (exchangeRate) {
    const symbol = plansService.getCurrencySymbol(exchangeRateSuffix);
    const formatted = financialService.formatNumber(exchangeRate, { decimals: 2, locale: 'pt-BR' });
    exchangeRateLabel = `${symbol} ${formatted}`;
  }

  const avatarSrc = op?.avatar?.base64Thumb || op?.avatar?.storageUrl || op?.avatar?.thumbUrl;

  const bgStripStyle = {
    ...STYLES.bgStrip,
    backgroundColor: baseColor,
    backgroundImage: `url(${BG_IMAGE_PATH})`,
  };

  const avatarContainerStyle = {
    width: AVATAR_SIZE,
    height: AVATAR_SIZE,
    borderRadius: 8,
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    border: `1px solid ${token.colorBorder}`,
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Background Header Area */}
      <div style={{ position: 'relative', height: 140 }}>
        {/* Colored Background with Image */}
        <div style={bgStripStyle} />

        {/* Operator Avatar — centered, overlapping background */}
        <div style={STYLES.avatarWrapper}>
          <div style={avatarContainerStyle}>
            <SimpleAvatar
              src={avatarSrc}
              name={op?.name || r?.name}
              size={AVATAR_SIZE}
              shape="square"
              showShadow={false}
              backColor={op?.backColor}
              foreColor={op?.foreColor}
            />
          </div>
        </div>
      </div>

      {/* Content below avatar */}
      <div style={{ textAlign: 'center' }}>
        {/* Tags: status, currency, API indicator */}
        <div style={STYLES.tagsRow}>
          {r?.status && <Tag color="blue">{translateX(r.status)}</Tag>}
          {r?.currency && <Tag>{r.currency}</Tag>}
          {isApiPlan && (
            <Tag color="green" icon={<ApiOutlined />}>
              API
            </Tag>
          )}
        </div>

        <Divider style={{ margin: '16px 0' }} />

        {/* Plan Name */}
        <Title level={4} style={{ marginBottom: 4 }}>
          {r?.name || '—'}
        </Title>

        {/* Operator Name */}
        {op?.name && (
          <Text type="secondary" style={{ fontSize: 13 }}>
            {op.name}
          </Text>
        )}

        {/* Info Rows */}
        <div style={STYLES.infoRows}>
          <Flex vertical gap={4} style={{ width: '100%' }}>
            <InfoRow label={translateX('cost')} value={priceLabel} strong />
            {exchangeRateLabel && (
              <InfoRow label={translateX('exchange_rate')} value={exchangeRateLabel} />
            )}
            {coverageCount > 0 && (
              <InfoRow label={translateX('coverages')} value={String(coverageCount)} />
            )}
            {isApiPlan && r._integrations.originalName && (
              <InfoRow label={translateX('original_name')} value={r._integrations.originalName} />
            )}
          </Flex>
        </div>

        <Divider style={{ margin: '24px 0 16px' }} />
      </div>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default PlansPreviewHeader;

// *******************************************************************************
// HELPERS
// *******************************************************************************

function InfoRow({ label, value, strong = false }) {
  if (!value) return null;

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <Text type="secondary">{label}:</Text>
      <Text strong={strong}>{value}</Text>
    </div>
  );
}
